/* Copyright (c) 2019-2025, NVIDIA CORPORATION.  All rights reserved.
 *
 * NVIDIA CORPORATION and its licensors retain all intellectual property
 * and proprietary rights in and to this software, related documentation
 * and any modifications thereto.  Any use, reproduction, disclosure or
 * distribution of this software and related documentation without an express
 * license agreement from NVIDIA CORPORATION is strictly prohibited.
 */

import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, mergeMap } from 'rxjs/operators';
import { BridgeCommands } from 'bridge-commands/bridge.commands';
import { Logger } from '../logging/logging.service';
import { BridgeMockService } from './bridge.mocks.service';
import { BridgeService, FullBridgeCommand } from './bridge.service';
import { PluginManager } from './common/plugin-manager.service';
import { PluginCommandMap, PluginHandlers, PluginResponse } from './common/plugin.models';

@Injectable()
export abstract class PluginService extends BridgeService {
    protected static EXTENSION_MESSAGE = 'QUERY_IPC_EXTENSION_MESSAGE';

    protected handlerMap: Map<string, PluginCommandMap>;

    /**
     * Constructor
     *
     * @param primaryManager - this plugin will implement the functions needed for this service
     * @param fallbackManager - this optional plugin will provide fallbacks for the functions not implemented by the primary manager.
     * In case of duplication between primary and fallback, primary will overrrule
     */
    constructor(
        logger: Logger,
        private primaryManager: PluginManager,
        private fallbackManager?: PluginManager,
        bridgeMocks?: BridgeMockService
    ) {
        super(logger, bridgeMocks);
        this.handlerMap = new Map<string, PluginCommandMap>();
        this.buildHandlerMap();
    }

    /**
     * Builds a map of bridge commands to their browser implementations
     * with the help of browser plugin manager.
     * Always set the fallback first, so that the primary can overwrite any duplicate handlers
     * between the 2.
     */
    private buildHandlerMap() {
        this.fallbackManager?.plugins?.forEach(plugin => {
            const handler: PluginHandlers = plugin.handlers;
            this.handlerMap.set(handler.module, handler.commands);
        });

        for (const plugin of this.primaryManager.plugins) {
            const handler: PluginHandlers = plugin.handlers;
            this.handlerMap.set(handler.module, handler.commands);
        }
    }

    /**
     * Get command implementation through plugin handler.
     */
    private getHandlerForMethod(module: string, method: string): (command: any) => Observable<PluginResponse> {
        const handlers = this.handlerMap.get(module);
        if (!handlers) {
            return null;
        }
        return handlers[method];
    }

    /**
     * Execute command using browser plugin.
     */
    protected executeCommandUsingPlugin<ParamType extends object, ResponseType>(
        command: BridgeCommands.Command<ParamType, ResponseType>,
        params: ParamType,
        shouldLogPayload: boolean,
        shouldIncludePayloadInError: boolean
    ): Observable<ResponseType> {
        const fullBridgeCommand: FullBridgeCommand<ParamType> = this.makeFullBridgeCommand(
            command,
            params,
            command.legacyCommand !== PluginService.EXTENSION_MESSAGE
        );

        const handlerFn = this.getHandlerForMethod(fullBridgeCommand.module, fullBridgeCommand.method);

        if (!handlerFn) {
            this.logger.info('plugin does not have handler for', fullBridgeCommand.module, fullBridgeCommand.method);
            return of(null);
        }

        return new Observable(obs => {
            handlerFn(fullBridgeCommand)
                .pipe(
                    catchError((err: PluginResponse) => {
                        this.logger.info('failed to execute', command.legacyCommand, err);

                        return new Observable<ResponseType>(observer => {
                            this.handleFailure(
                                observer,
                                fullBridgeCommand,
                                err.code,
                                err.error,
                                shouldLogPayload,
                                shouldIncludePayloadInError
                            );
                        });
                    }),
                    mergeMap(
                        (response: PluginResponse) =>
                            new Observable<ResponseType>(observer => {
                                this.handleSuccess(observer, fullBridgeCommand.persistent, response.data);
                            })
                    )
                )
                .subscribe(
                    data => obs.next(data),
                    error => obs.error(error),
                    () => obs.complete()
                );
        });
    }
}
